import React, {
  createContext,
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { signOut, onAuthStateChanged, User } from "firebase/auth";
import { auth } from "../config/firebase";
import { UserDetail } from "@/models";
import { getUserProfile } from "@/services";
import { DEFAULT_TENANT_ID, ROUTE_PATHS } from "@/constants";
import {
  getStoredTenantId,
  setStoredTenantId,
  removeStoredTenantId,
} from "@/utils/helper/tenant-storage";

interface AuthContextType {
  user: User | null;
  userDetail: UserDetail | null;
  token: string | null;
  loading: boolean;
  stalled: boolean;
  error: string | null;
  logout: () => Promise<void>;
  retryProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userDetail: null,
  token: null,
  loading: true,
  stalled: false,
  error: null,
  logout: async () => {},
  retryProfile: async () => {},
});

// Watchdog for the auth bootstrap. If `loading` hasn't dropped within this
// window — onAuthStateChanged never firing, getIdToken or the profile request
// hanging on a dead connection — consumers get `stalled: true` and must offer
// a reload instead of an eternal "Loading...". Longer than the axios timeout
// so a slow-but-answering backend surfaces as a profile `error` (retryable
// in place) rather than a stall.
const AUTH_STALL_TIMEOUT_MS = 30_000;

// The backend rejects every authenticated call with 403 "Tenant not found" /
// "Tenant database not configured" when the request carries no x-tenant-id
// header or the master tenant_users mapping is missing. Such a session can
// never recover on its own, so we force a clean re-login (which also lets the
// backend self-heal a missing mapping). Newer backends attach a stable
// TENANT_* code; older ones only have the message.
const isTenantForbiddenError = (error: unknown): boolean => {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: unknown; code?: unknown } };
    }
  )?.response;
  if (!response) return false;
  const code = response.data?.code;
  if (typeof code === "string" && code.startsWith("TENANT_")) return true;
  if (response.status !== 403) return false;
  const message = response.data?.message;
  return (
    typeof message === "string" && message.toLowerCase().includes("tenant")
  );
};

// QTY-1697: the backend rejects every member-facing call from an INACTIVE
// member with this stable code. Such a session can never recover either, so
// force a re-login; LoginPage surfaces the message via ?error=member_inactive.
const isMemberInactiveError = (error: unknown): boolean => {
  const response = (error as { response?: { data?: { code?: unknown } } })
    ?.response;
  return response?.data?.code === "MEMBER_INACTIVE";
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  // Preserve the tenant so a fresh login targets the same club, then hard
  // redirect to /login. Best-effort: never throw.
  const forceReLogin = useCallback(
    async (destination?: string): Promise<void> => {
      try {
        await signOut(auth);
      } catch {
        // ignore — we're abandoning this session either way
      }
      try {
        const tenantId = getStoredTenantId();
        globalThis.localStorage.clear();
        if (tenantId) {
          setStoredTenantId(tenantId);
        }
      } catch {
        // ignore
      }
      globalThis.location.href = destination ?? ROUTE_PATHS.AUTH.LOGIN;
    },
    [],
  );

  const loadUserProfile = useCallback(async (): Promise<void> => {
    try {
      const userProfile = await getUserProfile();
      if (!mountedRef.current) return;
      setUserDetail(userProfile);
      setError(null);
    } catch (e) {
      if (isTenantForbiddenError(e)) {
        await forceReLogin();
        return;
      }
      if (isMemberInactiveError(e)) {
        await forceReLogin(`${ROUTE_PATHS.AUTH.LOGIN}?error=member_inactive`);
        return;
      }
      if (!mountedRef.current) return;
      // Surface the failure instead of silently rendering private pages with
      // a null profile. Consumers can show this and call retryProfile().
      setUserDetail(null);
      setError("ไม่สามารถโหลดข้อมูลสมาชิกได้ กรุณาลองใหม่อีกครั้ง");
    }
  }, [forceReLogin]);

  useEffect(() => {
    // Reset for StrictMode's dev-only mount → cleanup → mount cycle.
    mountedRef.current = true;
    ensureTenantId();

    // Anything in the bootstrap below that never settles would otherwise
    // leave `loading` true forever with no signal to the user. The timer only
    // ever fires while the gate is still up: a completed bootstrap clears it.
    const stallTimer = globalThis.setTimeout(() => {
      if (mountedRef.current) setStalled(true);
    }, AUTH_STALL_TIMEOUT_MS);

    const handleUser = async (firebaseUser: User | null): Promise<void> => {
      if (!mountedRef.current) return;
      try {
        setUser(firebaseUser);
        if (firebaseUser) {
          ensureTenantId();
          const idToken = await firebaseUser.getIdToken();
          if (!mountedRef.current) return;
          setToken(idToken);
          await loadUserProfile();
        } else {
          setToken(null);
          setUserDetail(null);
          setError(null);
        }
      } catch {
        // getIdToken (or anything else above) failed — surface it instead of
        // silently rendering private pages with a null profile.
        if (mountedRef.current) {
          setError("ไม่สามารถกู้คืนเซสชันได้ กรุณาลองใหม่อีกครั้ง");
        }
      } finally {
        globalThis.clearTimeout(stallTimer);
        if (mountedRef.current) {
          setStalled(false);
          setLoading(false);
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      void handleUser(firebaseUser);
    });

    return () => {
      mountedRef.current = false;
      globalThis.clearTimeout(stallTimer);
      unsubscribe();
    };
  }, [loadUserProfile]);

  const logout = useCallback(async () => {
    await signOut(auth);
    const tenantId = getStoredTenantId();
    globalThis.localStorage.clear();
    if (tenantId) {
      setStoredTenantId(tenantId);
    }
    // Optionally clear app-specific state here
  }, []);

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({
          user,
          userDetail,
          token,
          loading,
          stalled,
          error,
          logout,
          retryProfile: loadUserProfile,
        }),
        [
          user,
          userDetail,
          token,
          loading,
          stalled,
          error,
          logout,
          loadUserProfile,
        ],
      )}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const ensureTenantId = (): void => {
  try {
    const searchParams = new URLSearchParams(globalThis.location.search);
    let tenantIdFromQuery =
      searchParams.get("tanantId") || searchParams.get("tenantId");

    if (!tenantIdFromQuery) {
      const liffState = searchParams.get("liff.state");
      if (liffState) {
        try {
          const liffStateParams = new URLSearchParams(
            liffState.startsWith("?") ? liffState : `?${liffState}`,
          );
          tenantIdFromQuery =
            liffStateParams.get("tanantId") || liffStateParams.get("tenantId");
        } catch {
          // no-op
        }
      }
    }

    if (tenantIdFromQuery) {
      setStoredTenantId(tenantIdFromQuery);
      return;
    }

    // getStoredTenantId also restores the localStorage copy from the cookie
    // mirror when the LINE webview evicted localStorage.
    const stored = getStoredTenantId();
    if (stored) {
      // Re-assert both stores: seeds the cookie for sessions that predate
      // the mirror and refreshes its Max-Age on every visit.
      setStoredTenantId(stored);
      return;
    }

    const normalizedDefault =
      DEFAULT_TENANT_ID && DEFAULT_TENANT_ID !== "undefined"
        ? DEFAULT_TENANT_ID
        : "";
    if (normalizedDefault) {
      setStoredTenantId(normalizedDefault);
    } else {
      removeStoredTenantId();
    }
  } catch {
    // no-op for non-browser environments
  }
};
